/**
 * Base for ForeTees API record editors
 * TODO:  Break ths up in to multiple mixins
 */
import NavRoute from '../navRoute'
import HeaderMethods from '../headerMethods'
import Rules from '../rules'
import ErrorHandler from '../errorHandler'
import boolean from '../boolean'
import {setItems, deepClone, forceFloat, forceInt, forceCurrency, displayPercent, extendBuildJsonPath} from '../../util/helpers'
import moment from 'moment'
import cloner from './mixins/cloner'
import apiHelpers from './mixins/apiHelpers'
import DetailEditorTable from '../../components/DetailEditorTable'
import ItemEditorDialog from '../../components/ItemEditorDialog'
import ItemEditorForm from '../../components/ItemEditorForm'
import ExpansionCard from '../../components/ExpansionCard'

// Something with Vue's reactive properties prevents us from putting this dialog object in data.
var exitPrompt = null // eslint-disable-line

export default {
  components: {DetailEditorTable, ItemEditorDialog, ItemEditorForm, ExpansionCard},
  mixins: [apiHelpers, cloner, HeaderMethods, Rules, NavRoute, ErrorHandler, boolean],
  data () {
    return {
      selectedItemId: undefined,
      selectedItemIdOrig: undefined,
      formReady: false,
      selectedPanel: this.defaultPanel,
      search: '',
      items: [],
      itemDefault: {},
      itemData: deepClone(this.itemDefault),
      isNewItem: false,
      isDirty: false,
      readOnly: false,
      disableButtons: false,
      originalItem: null,
      lastVal: null,
      resettingDirty: 0,
      disableAdd: false,
      momentFormLoaded: null,
      skipRecordLock: false,
      itemHideDelayTimer: null,
      delayItemHide: false
    }
  },
  props: {
    value: [Number, String, Object],
    cloneId: [Number, String],
    idQuery: {
      type: String,
      default: 'id'
    },
    focusAudit: Boolean,
    cloneIdQuery: {
      type: String,
      default: 'clone'
    }
  },
  computed: {
    jsonPath () {
      return '$[0]' // Always just the first record off root
    },
    forceReadOnly () {
      return false
    },
    showItem () {
      return this.selectedItemId !== undefined
    },
    showItemDelayedHide () {
      return !!this.showItem || !!this.delayItemHide
    },
    isPlaceholder () {
      return false
    },
    todaysDate () {
      return moment().format('YYYY-MM-DD')
    },
    maxSelectDate () {
      // By default, clamp max select date to now + 10 years
      return moment().add(10, 'years').format('YYYY-MM-DD')
    },
    pageTitle () {
      return (this.isNewItem ? 'New ' : this.readOnly ? 'View ' : 'Edit ') + this.itemPageTitle
    },
    itemPageTitle () {
      return this.type
    },
    isFormDisabled () {
      return this.readOnly || this.isDataLoading
    },
    isSaveDisabled () {
      return this.isFormDisabled || this.disableButtons
    },
    isDeleteDisabled () {
      return this.isSaveDisabled || this.disableButtons || this.isDeleteHidden
    },
    isDeleteHidden () {
      return false
    },
    instanceKey () {
      return this.isNewItem ? this.isPlaceholder ? 'placeholder' : 'new' : this.selectedItemId
    },
    useItemAuditHistory () {
      return true
    },
    // TODO:  Clean this up
    defaultContextMenu () {
      var results = []
      // Item editor or audit view
      if (!this.isNewItem && this.useItemAuditHistory) {
        results.push(...[
          {
            title: 'Show Audit History',
            icon: 'mdi-history',
            action: () => this.selectedPanel = this.auditPanel,
            hide: !this.editPanelActive
          },
          {
            title: 'Show Editor',
            icon: 'mdi-pencil',
            action: () => this.selectedPanel = this.editPanel,
            hide: this.editPanelActive
          },
          {hide: !this.editPanelActive} // Divider
        ])
      }
      if (!this.isNewItem) {
        results.push(...[
          {
            title: 'Delete',
            icon: 'mdi-delete-forever',
            action: this.deleteWithConfirmation,
            hide: !this.editPanelActive || this.isDeleteHidden,
            disabled: this.isDeleteDisabled
          }
        ])
        if (this.cloner) {
          results.push({
            title: 'Clone',
            icon: 'mdi-content-duplicate',
            action: this.cloneItem,
            hide: !this.editPanelActive
          })
        }
      }
      results.push({
        title: 'Save',
        icon: 'mdi-content-save',
        action: this.saveItem,
        hide: !this.editPanelActive,
        disabled: this.isSaveDisabled
      })
      return results
    },
    editPanelActive () {
      return this.selectedPanel === this.editPanel
    },
    editPanel () {return 'itemEditor'},
    auditPanel () {return 'itemAudit'},
    defaultPanel () {return this.editPanel},
    lockTimerSettings () {
      return {
        endTime: this.itemData.recordLockExpiration,
        onTimeout: this.releaseItemLock,
        onAccept: this.forceBackToCaller,
        renewCb: this.renewLock
      }
    },
    auditHistorySettings () {
      return {
        recordType: this.apiRecordCommand,
        id: this.itemData.id,
        created: this.itemData.created,
        updated: this.itemData.updated
      }
    },
    pageBarButtons () {
      return [
        {
          text: this.isNewItem ? /* 'Create' */ 'Save' : 'Save',
          mobileIcon: 'mdi-content-save',
          action: this.saveItem,
          disabled: this.isSaveDisabled,
          hide: false,
          color: this.isDirty ? this.$ftStyle.pageBarSaveDirtyButtonColor : this.$ftStyle.pageBarSaveButtonColor,
          loading: this.isDataLoading
        }
      ]
    }
  },
  methods: {
    forceInt,
    forceFloat,
    forceCurrency,
    displayPercent,
    buildJsonPath () {
      var path = this.jsonPath
      for (var i = 0, l = arguments.length; i < l; i++) {
        path = extendBuildJsonPath(path, arguments[i])
      }
      return path
    },
    extJsonPath (ext) {
      return extendBuildJsonPath(this.jsonPath, ext)
    },
    releaseItemLock (callback = () => {}) {
      // If we were in the middle of exiting, cancel that
      if (exitPrompt && exitPrompt.showDialog) {
        exitPrompt.close()
      }
      // Release record locks
      this.$ftAPI().release(() => {
        callback()
      }, {loading: this.setIsLoading})
    },
    renewLock (dialog) {
      this.$ftAPI().closeDialogs()
      dialog.isLoading = true
      this.doRenewLock(
        // Success
        () => {
          dialog.isLoading = false
          dialog.close()
          this.disableButtons = false
        },
        // Fail
        (e) => {
          this.$vPrompt({
            modal: true,
            title: 'Unable to Renew Lock',
            titleIcon: 'mdi-alert-circle-outline',
            titleIconColor: 'error',
            text: e.error
          })
          dialog.isLoading = false
          console.log('Renew Lock Error', e)
        }
      )
    },
    doRenewLock (success, fail) {
      return this.doRenewLockCore(success, fail)
    },
    doRenewLockCore (success, fail) {
      const itemCommand = this.getItemCommand(this.itemData.id)
      const renewCommand = {
        command: 'renewLock',
        error: fail,
        load: (r) => {
          this.updateRecordLocks(r.key, r.expiration, () => {
            console.log('Renew Lock Success', r)
            success()
          }, fail)
        },
        params: {
          record_key: this.itemData.recordLockKey,
          record_hash: this.itemData.recordHash,
          id: this.itemData.id,
          record_type: this.apiRecordCommand
        }
      }
      if (itemCommand.params) {
        // Make sure we include same field as item, else hash will be off
        if (itemCommand.params.children) {
          renewCommand.params.children = itemCommand.params.children
        }
        if (itemCommand.params.include_fields) {
          renewCommand.params.include_fields = itemCommand.params.include_fields
        }
        if (itemCommand.params.exclude_fields) {
          renewCommand.params.exclude_fields = itemCommand.params.exclude_fields
        }
      }
      this.$ftAPI().get([renewCommand])
    },
    updateRecordLocks (lockKey, expiration, success, fail) {
      const isDirty = this.isDirty
      this.setRecordLocksInObject(this.itemData, this.itemData.recordLockKey, lockKey, expiration)
      this.resetDirty(isDirty)
      success()
    },
    setRecordLocksInObject (object, oldKey, lockKey, expiration) {
      if (!object) return
      if (Array.isArray(object)) {
        for (let item of object) {
          this.setRecordLocksInObject(item, oldKey, lockKey, expiration)
        }
      }
      for (let key in object) {
        let item = object[key]
        if (key === 'recordLockKey' && (item === oldKey || !oldKey && !item)) {
          object[key] = lockKey
          object.recordLockExpiration = expiration
        } else if (typeof item === 'object') {
          this.setRecordLocksInObject(item, oldKey, lockKey, expiration)
        }
      }
    },
    getItemDataChanges () {
      return false
    },
    deleteCommand () {
      return {
        command: this.apiRecordCommand,
        load: this.backToCaller,
        error: this.apiItemError,
        recordLockKey: this.itemData.recordLockKey,
        name: this.type,
        loading: this.setIsLoading
      }
    },
    deleteCommandFilter (command) {
      return command
    },
    deleteWithConfirmation () {
      this.$ftAPI().deleteWithConfirmation(this.addResourceErrorHandler(this.deleteCommandFilter(this.deleteCommand())), this.itemData)
    },
    isFormValid () {
      console.log('validating form', this.$refs.editForm)
      if (this.$refs.editForm) {
        return this.$refs.editForm.validate()
      }
      return this.formReady
    },
    saveCommandFilter (command) {
      // Overridden by component.  Do not put code here
      return command
    },
    afterSaveCommandCall (r) {
      this.afterSave(r)
      this.resetDirty()
      this.backToCaller()
    },
    saveCommand () {
      const command = {
        command: this.apiRecordCommand,
        load: this.afterSaveCommandCall,
        fieldError: this.setFieldError,
        error: this.apiItemError,
        cancelled: this.apiSubmitCancelled, // error in submit, confirmation cancelled, etc.
        loading: this.setIsLoading
      }
      if (this.skipRecordLock) {
        command.params = command.params || {}
        command.params.nolock = true
      }
      return command
    },
    modifyItemDataBeforeSave (data) {
      // Don't add anything here.  Override to add functionality.
    },
    beforeSaveItem (saveItemCallback) {
      // Keep simple for override
      // Don't add anything here.  Override to add functionality.
      saveItemCallback()
    },
    saveItem (onError) {
      this.isLoading++
      // Give form elements a chance to update before submitting
      setTimeout(() => {
        // Give lazy form elements even more of a chance to update before submitting
        setTimeout(() => {
          this.isLoading--
          console.log('Saving form')
          if (!this.isFormValid()) {
            console.log('Form is invalid')
            this.errors = []
            setTimeout(() => {
              this.errors = ['Invalid values. Check and try again.']
            }, 0)
            return
          }
          const saveItemCallback = () => {
            this.resetValues(true)
            const saveCommand = this.addResourceErrorHandler(this.saveCommandFilter(this.saveCommand()))
            if (typeof onError === 'function') {
              const onCancelled = saveCommand.cancelled
              saveCommand.cancelled = (ftresponse) => {
                onError(ftresponse)
                onCancelled(ftresponse)
              }
            }
            this.modifyItemDataBeforeSave(this.itemData)
            var itemDataChanges = this.getItemDataChanges()
            if (itemDataChanges === false) {
              // Not using change detection
              itemDataChanges = this.itemData
            } else if (!itemDataChanges) {
              // No item data to save
              setTimeout(() => {this.afterSaveCommandCall(this.itemData)}, 0)
            }
            this.$ftAPI().put(saveCommand, itemDataChanges)
          }
          this.beforeSaveItem(saveItemCallback)
        }, 100)
      }, 0)
    },
    beforeLoadData () {
      return true
    },
    loadData () {
      if (!this.showItem) return
      console.log('Loading item data')
      if (!this.beforeLoadData()) return
      console.log('Loading item ID', this.selectedItemId)
      this.isLoading = 0
      var id = this.selectedItemId
      this.resetValues()
      this.isNewItem = !id
      if (id !== undefined) {
        // Show item
        this.itemData = deepClone(this.itemDefault)
        this.loadItem(id)
      }
    },
    itemResourceCommands () {
      // Overridden by component.  Do not put code here
      return []
    },
    beforeLoad (data) {
      // Overridden by component.  Do not put code here
    },
    beforeItemLoad (data) {
      // See if component has anything else to do
      this.beforeLoad(data)
      // Reset our error lookup
      this.resetFieldErrorLookup(data)
      this.lastVal = JSON.parse(JSON.stringify(data))
      // When editing, store original, unedited, reservation
      if (!this.isNewItemisNewItem) this.originalItem = deepClone(data)
    },
    afterItemLoad (r) {
      this.momentFormLoaded = this.$ftAPI().clubMoment()
      this.afterLoad(r)
      console.log('Loaded Item', this.itemData)
      this.resetItemFieldErrorLookup()
      this.resetDirty()
    },
    itemCommand (id) {
      return {
        command: this.apiRecordCommand,
        beforeLoad: this.beforeItemLoad,
        load: this.itemData,
        loading: this.setIsLoading,
        error: this.apiItemError,
        afterLoad: this.afterItemLoad,
        lockFault: () => {this.loadItem(id, true, true)},
        params: {id: id, lock: !this.skipRecordLock, include_fields: ['created', 'updated'], include_hash: true}
      }
    },
    itemCommandFilter (command) {
      // Overridden by component.  Do not put code here
      return command
    },
    getItemCommand (id) {
      return this.itemCommandFilter(this.itemCommand(id))
    },
    loadItem (id, readOnly, skipReset) {
      console.log('Loading item ID', id)
      this.readOnly = this.forceReadOnly || readOnly === true
      if (!skipReset) this.resetValues()
      var commands = [
        ...this.itemResourceCommands(id)
      ]
      if (id) {
        // Load existing item
        commands.push(this.getItemCommand(id))
      } else {
        // Creating a new item.  For now, just load current user so we can do something to bind afterLoad to.
        // Probably should create a very lightweight API call, like current system time, for this kind of thing.
        commands.push({
          command: 'currentUser',
          load: () => {
            // Cloning?
            if (this.cloneId) {
              const clone = this.getClone(this.cloneId)
              if (clone) {
                setItems(this.itemData, clone)
              }
              this.resetFieldErrorLookup(this.itemData)
              console.log('Cloning', clone, this.itemData)
            }
          },
          beforeLoad: () => {this.beforeItemLoad(this.itemData)},
          afterLoad: () => {this.afterItemLoad(this.itemData)}
        })
      }
      if (this.readOnly) {
        // We're read only.  Remove any record lock requests
        for (let i = 0, l = commands.length; i < l; i += 1) {
          var command = commands[i]
          if (command.params && command.params.lock) {
            command.params.lock = false
          }
        }
      }
      if (commands.length) {
        this.$ftAPI().get(this.addResourceErrorHandler(commands))
      }
    },
    beforeItemsLoad (data) {
      // Do nothing here. Overridden by Component
    },
    resetDirty (val = false) {
      if (!val) {
        this.isDirty = false
        this.resettingDirty += 1
        setTimeout(() => {
          this.isDirty = false
          this.resettingDirty -= 1
        }, 0)
      }
    },
    afterLoad (r) {
      // Don't put code here.  Will be overridden by instance
    },
    afterSave (r) {
      // Don't put code here.  Will be overridden by instance
    },
    resetLocalValues (keepItemData) {
      // Overridden by component.  Do not put code here
      return
    },
    // Badly named method?
    resetValues (keepItemData) {
      console.log('Resetting values', keepItemData)
      this.resetLocalValues(keepItemData)
      if (!keepItemData) {
        this.momentFormLoaded = null
        this.selectedPanel = this.focusAudit ? this.auditPanel : this.defaultPanel
        setItems(this.itemData, this.itemDefault)
        this.resetDirty()
      }
      this.resetFieldErrorLookup(this.itemData)
      this.resetApiErrors()
    },
    resetItemFieldErrorLookup () {
      this.resetFieldErrorLookup(this.itemData, true)
    },
    forceBackToCaller () {
      this.resetDirty()
      this.backToCaller()
    },
    backToCaller () {
      this.$ftAPI().closeDialogs()
      if (this.$route.query.eoc) {
        console.log('Closing item edit browser window/tab')
        window.close()
      } else {
        console.log('Backing out from item edit screen')
        this.$router.go(-1)
      }
    },
    promptReleaseLock (confirmAction) {
      this.promptExit(() => {
        this.releaseItemLock(() => {
          confirmAction()
        })
      }, () => {})
    },
    promptExit (confirmAction, cancelAction) {
      var _this = this
      if (!_this.readOnly && _this.isDirty) {
        exitPrompt = _this.$vPrompt({
          title: 'Unsaved Changes',
          titleIcon: 'mdi-alert-circle',
          titleIconColor: 'warning',
          text: 'Discard unsaved changes to this ' + _this.type + '?',
          confirmText: 'Discard',
          confirmAction: confirmAction,
          cancelAction: cancelAction
        })
      } else {
        confirmAction()
      }
    },
    doExit (exitAction, noExitAction) {
      this.promptExit(() => {
        this.readOnly = true
        this.releaseItemLock(() => {
          if (exitAction) exitAction()
        })
      }, () => {
        if (noExitAction) noExitAction()
      })
    },
    doClose () {
      this.doExit(this.backToCaller)
    },
    beforeParentRouteChange (to, from, next) {
      // Navigating to different component
      console.log('Changing routes??', to, from)
      if (!this.showItem) next()
      this.doExit(next, () => {next(false)})
    }
  },
  created () {
    console.log('created', this)
    // this.resetValues()
    // if ('didCreate' in this) {
    //   this.didCreate = true
    // }
    this.resetValues()
  },
  beforeDestroy () {
    this.releaseItemLock()
  },
  /*
  beforeMount () {
    console.log('I\'m a little tea cup')
    this.resetValues()
  },
  */
  mounted () {
    this.selectedItemId = this.value
    this.selectedItemIdOrig = this.value
  },
  watch: {
    itemData: {
      handler (val) {
        // console.log('Item change detected', newVal, oldVal)
        if (this.resettingDirty) return
        this.isDirty = true
        // this.resetFieldErrorLookup(this.itemData, true)
      },
      deep: true
    },
    isDirty () {
      console.log('isDirty', this.isDirty)
    },
    value (val) {
      console.log('Editing Item ID Changed', val)
      this.selectedItemId = val
      if (val) this.selectedItemIdOrig = val
    },
    selectedItemId (val, oldVal) {
      console.log('Editing Selected Item ID Changed', val)
      if (val !== this.value) {
        this.$emit('input', val)
      }
      if (val !== undefined && this.showItem) {
        this.resetValues()
        this.loadData()
      }
      if (oldVal && val !== oldVal) {
        this.releaseItemLock()
      }
    },
    $route () {
      // if (this.showItem) this.resetValues()
    },
    showItem (val, oldVal) {
      if (val) {
        if (this.itemHideDelayTimer) clearTimeout(this.itemHideDelayTimer)
        this.delayItemHide = true
      } else {
        this.itemHideDelayTimer = setTimeout(() => {this.delayItemHide = false}, 1000)
      }
    }
  },
  updated () {
    if (!this.selectedPanel) {
      this.selectedPanel = this.defaultPanel
    }
  }
}
